'use client';

import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'next-i18next';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'react-redux';

import {
    Box,
    Flex,
    GridItem,
    Heading,
    HStack,
    Text,
    useSlotRecipe,
    VStack,
} from '@chakra-ui/react';

import isNil from 'lodash/isNil';

import { TileListing } from '@nocowanie/noc-api';

import { LoadingState } from '@nocowanie/core/enums/loading-state.enum';
import { DataLayerPageTypeEnum } from '@nocowanie/core/models';
import { GridWrapper } from '@nocowanie/common-ui/components/atoms/grid-wrapper';
import { SeoLinks } from '@nocowanie/common-ui/components/molecules/seo-links';
import { usePluralisationTranslation } from '@nocowanie/common-ui/hooks';
import { IconsLinear } from '@nocowanie/icons/linear';

import {
    AppStore,
    commonUiStateSlice,
    initSearchFromUrlAction,
    listingStateSlice,
    searchStateSlice,
    setSearchLoadingAction,
    useAppDispatch,
    useAppSelector,
} from '@app/app-state';
import { AnimatedCollapse } from '@app/components/atoms/animated-collapse';
import { ListingBreadcrumbs } from '@app/components/atoms/listing-breadcrumbs';
import { ListingDescription } from '@app/components/atoms/listing-description';
import { MessageBox } from '@app/components/atoms/message-box';
import { PageDataLayerEvents } from '@app/components/atoms/page-data-layer-events';
import { ResultsOrderInfo } from '@app/components/atoms/results-order-info';
import { AccommodationTileLite } from '@app/components/molecules/accommodation-tile-lite';
import { ErrorInfo } from '@app/components/molecules/error-info';
import { ListingActiveFilters } from '@app/components/molecules/listing-active-filters';
import { ListingFaq } from '@app/components/molecules/listing-faq';
import { ListingSearchBar } from '@app/components/molecules/listing-search-bar';
import { MapButton } from '@app/components/molecules/map-button';
import { MapEntryPoint } from '@app/components/molecules/map-entry-point';
import { NearbyAccommodationsMessage } from '@app/components/molecules/nearby-accommodations-message';
import { Pagination } from '@app/components/molecules/pagination';
import { SortingDrawer } from '@app/components/molecules/sorting-drawer';
import { SortingSelect } from '@app/components/molecules/sorting-select';
import { StickySpinner } from '@app/components/molecules/sticky-spinner';
import { SwitchStickyBar } from '@app/components/molecules/switch-sticky-bar';
import { ListingFilters } from '@app/components/organisms/listing-filters';
import { Support } from '@app/components/organisms/support';
import { ListingLayoutProps } from '@app/components/pages/listing';
import { SEARCH_CONSTS } from '@app/consts/consts';
import { ACCOMMODATION_TILE } from '@app/consts/listing.consts';
import { resolveListingHeader } from '@app/helpers/listing.helpers';
import { useNocSmListingSearchPageVisit } from '@app/hooks/sales-manago';
import { useActiveFilters } from '@app/hooks/useActiveFilters';
import { useAutoHeightTransition } from '@app/hooks/useAutoHeightTransition';
import { useDataLayerListingEvents } from '@app/hooks/useDataLayerListingEvents';
import { useListingSearch } from '@app/hooks/useListingSearch';
import { useSearch } from '@app/hooks/useSearch';
import { useSearchbarScrollBehavior } from '@app/hooks/useSearchbarScrollBehavior';
import {
    buildInitSearchPayload,
    useHasCookieSearchData,
    useSearchInitializer,
} from '@app/hooks/useSearchInitializer';
import { SearchCriteria } from '@app/models';
import { GeoCityModel } from '@app/models/geo.model';
import { setMetaHeader } from '@app/state/slices/listing-state.slice';

// Becomes `true` after the first listing has hydrated in this browser tab. Before that,
// listing/search state is initialized during render (same result on server and client),
// so hydration renders the final state and no Redux updates fire right after it.
let hasHydrated = false;

const buildListingSearchParams = (
    searchParams: URLSearchParams | null,
    initialLocationData?: Partial<SearchCriteria>,
): URLSearchParams => {
    const params = new URLSearchParams(searchParams?.toString());

    if (initialLocationData) {
        if (initialLocationData.geoId && !params.has('geoId')) {
            params.set('geoId', initialLocationData.geoId);
        }
        if (initialLocationData.locationType && !params.has('type')) {
            params.set('type', initialLocationData.locationType);
        }
        if (initialLocationData.location && !params.has('location')) {
            params.set('location', initialLocationData.location);
        }
    }

    return params;
};

export const ListingLayout = ({
    preloadedMeta,
    preloadedItems,
    preloadedSearchCriteria,
    initialLocationData,
    withMap = true,
    breadcrumbs,
    breadcrumbName,
    linkspace,
    cmsDescription,
    cmsFaq,
    filtersOverride,
    availableOptions,
}: ListingLayoutProps) => {
    const store = useStore() as AppStore;
    const searchParams = useSearchParams();
    const isInitializedInRender = useRef(false);
    const pendingCookieInitRef = useRef(false);

    // Must run before any `useAppSelector` below, so this render already reads the
    // initialized state (otherwise react-redux forces a synchronous re-render).
    if (!isInitializedInRender.current && (typeof window === 'undefined' || !hasHydrated)) {
        isInitializedInRender.current = true;

        if (preloadedItems?.data && preloadedMeta?.data) {
            store.dispatch(
                listingStateSlice.setMeta({ ...preloadedMeta, loading: LoadingState.SUCCESS }),
            );
            store.dispatch(
                listingStateSlice.setItems({ ...preloadedItems, loading: LoadingState.SUCCESS }),
            );
        }

        // Cookie is not available on the server; cookie-based criteria are applied in an effect
        store.dispatch(
            initSearchFromUrlAction(
                buildInitSearchPayload(
                    buildListingSearchParams(searchParams, initialLocationData),
                    {
                        campaignListingId: searchStateSlice.selectors.selectCampaignListingId(
                            store.getState(),
                        ),
                        cmsDates: {
                            checkIn: preloadedMeta?.data?.checkIn,
                            checkOut: preloadedMeta?.data?.checkOut,
                            flexDates: preloadedMeta?.data?.flexDates,
                        },
                        filtersOverride,
                    },
                ),
            ),
        );
    }

    const isStatePreloaded = useRef(isInitializedInRender.current);
    const skipFirstFetch = useRef(true);
    const hasPreloadedItems = useRef(preloadedItems?.data);
    const isInitialMount = useRef(true);
    const criteriaChangedRef = useRef(true);
    const willFetchRef = useRef(false);
    const fetchCounterRef = useRef(0);
    const dispatch = useAppDispatch();
    const { t } = useTranslation('listing');
    const { t: tileTranslations } = useTranslation('common', {
        keyPrefix: 'accommodationTile',
    });
    const { t: tMessage } = useTranslation('common', {
        keyPrefix: 'nearbyAccommodationsMessage',
    });
    const pluralizeTranslation = usePluralisationTranslation('listing');
    const pageItemCount = SEARCH_CONSTS.PAGE_ITEM_COUNT;

    const { isInitialRender, isMobileRes, toggleFiltersVisibility } = useSearch();
    const initializeSearch = useSearchInitializer({
        autoOpenFiltersOnDesktop: false,
    });
    const hasCookieSearchData = useHasCookieSearchData();
    const {
        campaignListingId,
        fetchListingItems,
        searchCriteria,
        filters,
        currentPage,
        isSearchLoading,
    } = useListingSearch();

    const { activeFilters } = useActiveFilters();
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const listingMeta = useAppSelector(listingStateSlice.selectors.selectListingMeta);
    const listingMetaData = useMemo(
        () =>
            listingMeta.data.header ? listingMeta.data : (preloadedMeta?.data ?? listingMeta.data),
        [listingMeta.data, preloadedMeta?.data],
    );

    const {
        containerRef: resultsSectionRef,
        contentRef: resultsContentRef,
        containerStyle: resultsSectionStyle,
    } = useAutoHeightTransition({
        shadowBuffer: 12, // covers shadows.tile's 10px blur
    });

    const {
        header,
        itemCount,
        cmsId,
        checkIn: cmsCheckIn,
        checkOut: cmsCheckOut,
        flexDates: cmsFlexDates,
        locationDetails,
    } = listingMetaData;

    const isListingItemsError = useAppSelector(
        listingStateSlice.selectors.selectIsListingItemsError,
    );
    const stateItems = useAppSelector(listingStateSlice.selectors.selectListingItems);

    useNocSmListingSearchPageVisit({
        activeFilters,
        searchCriteria,
    });

    const allItems = useMemo(() => {
        return stateItems ?? preloadedItems?.data?.tiles;
    }, [stateItems, preloadedItems?.data?.tiles]);

    const lastSearchTimestamp = useAppSelector(
        searchStateSlice.selectors.selectSearchLastTimestamp,
    );

    const effectiveSearchCriteria = useMemo(() => {
        if (lastSearchTimestamp || !preloadedSearchCriteria) {
            return searchCriteria;
        }

        return {
            ...searchCriteria,
            ...preloadedSearchCriteria,
            checkInDate: preloadedSearchCriteria.checkInDate,
            checkOutDate: preloadedSearchCriteria.checkOutDate,
            flexDates: preloadedSearchCriteria.flexDates,
        };
    }, [lastSearchTimestamp, searchCriteria, preloadedSearchCriteria]);

    const recipe = useSlotRecipe({ key: 'listingLayout' });
    const styles = recipe({});

    const [shouldFetchItems, setShouldFetchItems] = useState(false);
    const {
        anchorRef: searchbarAnchorRef,
        searchbarRef,
        isScrolledPast: isSearchbarSticky,
        searchbarTransform,
    } = useSearchbarScrollBehavior();
    const { sendSearchResultsDataLayerEvent } = useDataLayerListingEvents();

    useEffect(() => {
        if (!isStatePreloaded.current && hasPreloadedItems.current && preloadedMeta?.data) {
            dispatch(
                listingStateSlice.setMeta({
                    ...preloadedMeta,
                    loading: LoadingState.SUCCESS,
                }),
            );
            dispatch(
                listingStateSlice.setItems({
                    ...preloadedItems,
                    loading: LoadingState.SUCCESS,
                }),
            );
            isStatePreloaded.current = true;
        } else if (!isStatePreloaded.current) {
            console.warn('[ListingLayout] NOT dispatching - validation failed:', {
                hasPreloadedItems: !!preloadedItems?.data?.tiles,
                hasPreloadedMeta: !!preloadedMeta?.data,
            });
        }
    }, []);

    useEffect(() => {
        hasHydrated = true;

        const params = buildListingSearchParams(searchParams, initialLocationData);
        const needsCookieInit = hasCookieSearchData() && !params.has('checkInDate');

        if (needsCookieInit) {
            skipFirstFetch.current = false;
        }

        if (isInitializedInRender.current) {
            if (!needsCookieInit) {
                // Already initialized during render: there is no init update whose fetch
                // needs skipping, so the next criteria change must fetch.
                skipFirstFetch.current = false;
                return;
            }

            // Results for cookie criteria are refetched; report them instead of the SSR ones
            pendingCookieInitRef.current = true;
        }

        initializeSearch(
            params,
            campaignListingId,
            undefined,
            {
                checkIn: cmsCheckIn,
                checkOut: cmsCheckOut,
                flexDates: cmsFlexDates,
            },
            filtersOverride,
        );
    }, []);

    useEffect(() => {
        willFetchRef.current = !skipFirstFetch.current;

        // Skip only the initial fetch if we have preloaded items from server
        if (isInitialMount.current && hasPreloadedItems.current) {
            isInitialMount.current = false;
            return;
        }

        pendingCookieInitRef.current = false;

        isInitialMount.current = false;
        setShouldFetchItems(true);
    }, [campaignListingId, currentPage, filters, searchCriteria]);

    useEffect(() => {
        if (skipFirstFetch.current || !shouldFetchItems || campaignListingId) {
            return;
        }

        dispatch(
            setMetaHeader(
                resolveListingHeader(t, {
                    locationName: searchCriteria?.location,
                }),
            ),
        );
    }, [filters, searchCriteria?.location, shouldFetchItems, campaignListingId]);

    useEffect(() => {
        if (!shouldFetchItems) {
            return;
        }

        if (skipFirstFetch.current) {
            skipFirstFetch.current = false;
            return;
        }

        dispatch(setSearchLoadingAction(true));
        setShouldFetchItems(false);

        fetchCounterRef.current += 1;

        fetchListingItems();
    }, [shouldFetchItems, fetchListingItems, dispatch]);

    useEffect(() => {
        // Only finish a pending search: `setSearchLoadingAction(false)` also bumps
        // `lastSearchTimestamp`, which would re-render the whole layout right after hydration.
        if (searchStateSlice.selectors.selectIsSearchLoading(store.getState())) {
            dispatch(setSearchLoadingAction(false));
        }
        willFetchRef.current = false;
    }, [allItems]);

    useEffect(() => {
        criteriaChangedRef.current = true;
    }, [searchCriteria]);

    useEffect(() => {
        if (
            allItems &&
            lastSearchTimestamp &&
            criteriaChangedRef.current &&
            !willFetchRef.current &&
            !pendingCookieInitRef.current
        ) {
            criteriaChangedRef.current = false;
            sendSearchResultsDataLayerEvent();
        }
    }, [allItems, sendSearchResultsDataLayerEvent, lastSearchTimestamp]);

    useEffect(() => {
        if (!isListingItemsError) {
            return;
        }

        dispatch(setSearchLoadingAction(false));
    }, [isListingItemsError]);

    const startPosition = ((currentPage || 1) - 1) * pageItemCount + 1;

    // Detect first Nearby placement
    const placementChangeIndex = useMemo(
        () =>
            allItems?.findIndex(tile => tile?.listing?.placement === TileListing.placement.NEARBY),
        [allItems],
    );

    const renderBreadcrumbs = () => {
        if (!cmsId && breadcrumbs && breadcrumbName) {
            return <ListingBreadcrumbs breadcrumbs={breadcrumbs} name={breadcrumbName} />;
        }
    };

    const cityCenterLocation = useMemo(
        () =>
            !isNil(locationDetails)
                ? { lat: locationDetails.location.lat, lon: locationDetails.location.lon }
                : undefined,
        [locationDetails],
    );
    const cityNameVarieties = useMemo(
        () =>
            !isNil(locationDetails)
                ? {
                      nominative: locationDetails.name,
                      genitive: (locationDetails as GeoCityModel).varietyGenitive,
                      locative: locationDetails.varietyLocative,
                  }
                : undefined,
        [locationDetails],
    );

    const renderedTiles = useMemo(() => {
        if (isListingItemsError || !allItems?.length) {
            return null;
        }

        const hasDates =
            !!effectiveSearchCriteria.checkInDate && !!effectiveSearchCriteria.checkOutDate;

        return allItems.map((tileData, index) => (
            // We need to change tile `key` on each fetch to prevent CLS when tiles are rearranged
            // and same item is placed in different spot on the list
            <React.Fragment key={`${fetchCounterRef.current}-${tileData.id || index}`}>
                {placementChangeIndex === index && (
                    <NearbyAccommodationsMessage
                        locationName={effectiveSearchCriteria?.location}
                        withDates={hasDates}
                        t={tMessage}
                    />
                )}
                <AccommodationTileLite
                    position={startPosition + index}
                    searchCriteria={effectiveSearchCriteria}
                    searchFilters={filters}
                    initialData={tileData}
                    amenitiesMaxVisible={isMobileRes ? ACCOMMODATION_TILE.MAX_AMENITIES : undefined}
                    priorityCount={3}
                    cityLocation={cityCenterLocation}
                    locationVarieties={cityNameVarieties}
                    t={tileTranslations}
                    isMobileBrowser={isMobileBrowser}
                />
            </React.Fragment>
        ));
    }, [
        allItems,
        filters,
        isListingItemsError,
        isMobileRes,
        placementChangeIndex,
        effectiveSearchCriteria,
        startPosition,
        tMessage,
        tileTranslations,
        cityCenterLocation,
        cityNameVarieties,
        isMobileBrowser,
    ]);
    return (
        <>
            <PageDataLayerEvents pageType={DataLayerPageTypeEnum.Search} />
            <main>
                {isMobileRes && renderBreadcrumbs()}
                <Box ref={searchbarAnchorRef} />
                <Box
                    css={styles.searchbar}
                    boxShadow={isSearchbarSticky ? 'stickyHeader' : 'none'}
                    transform={isMobileRes ? searchbarTransform : undefined}
                    bg={isMobileRes || isSearchbarSticky ? 'white' : 'transparent'}
                >
                    <Box ref={searchbarRef}>
                        <ListingSearchBar
                            cmsCheckIn={cmsCheckIn}
                            cmsCheckOut={cmsCheckOut}
                            cmsFlexDates={cmsFlexDates}
                            locationPlaceholder={campaignListingId && header ? header : undefined}
                            mb={{ base: 2, md: 0 }}
                        />
                    </Box>
                    {!isListingItemsError ? (
                        <Box
                            css={styles.actionBar}
                            borderBottomColor={!isSearchbarSticky ? 'border' : 'transparent'}
                        >
                            {withMap ? <MapButton /> : null}
                            <ListingFilters variant="mobile" availableOptions={availableOptions} />
                            <SortingDrawer sortings={availableOptions?.sortings} />
                        </Box>
                    ) : null}
                </Box>
                {!isMobileRes && renderBreadcrumbs()}
                <GridWrapper
                    gridTemplateColumns={{
                        base: '1fr',
                        lg2: '320px auto',
                    }}
                    columnGap={{ base: 0, lg2: 8 }}
                    rowGap={0}
                >
                    <GridItem css={styles.sidebar}>
                        {withMap ? <MapEntryPoint /> : null}
                        <ListingFilters variant="desktop" availableOptions={availableOptions} />
                    </GridItem>
                    <GridItem as={VStack} alignItems={'stretch'} css={styles.searchResultsWrapper}>
                        <Box
                            ref={resultsSectionRef}
                            minH={isMobileBrowser ? '267px' : '293px'}
                            style={resultsSectionStyle}
                        >
                            <Box ref={resultsContentRef}>
                                {!isListingItemsError ? (
                                    <>
                                        <HStack css={styles.headingWrapper} alignItems={'start'}>
                                            <Flex css={styles.pageHeading}>
                                                <Heading as={'h1'}>{header}</Heading>
                                                <Text as={'span'} color={'gray.600'}>
                                                    {pluralizeTranslation(
                                                        itemCount,
                                                        'accommodation',
                                                    )}
                                                </Text>
                                            </Flex>
                                            <SortingSelect sortings={availableOptions?.sortings} />
                                        </HStack>

                                        {!isMobileRes ? (
                                            <AnimatedCollapse
                                                isOpen={
                                                    activeFilters.length > 0 && !isInitialRender
                                                }
                                                css={styles.activeFilters}
                                                mb={activeFilters.length > 0 ? 2 : 0}
                                            >
                                                <ListingActiveFilters />
                                            </AnimatedCollapse>
                                        ) : undefined}

                                        {!isListingItemsError ? <ResultsOrderInfo mb={2} /> : null}
                                    </>
                                ) : null}
                                <Box as={'section'}>
                                    {isListingItemsError ? (
                                        <ErrorInfo data-testid={'error-info'} mt={4} mb={8} />
                                    ) : !allItems?.length ? (
                                        <MessageBox
                                            title={t('noResults.title')}
                                            message={t('noResults.message')}
                                            mb={4}
                                            boxShadow={'tile'}
                                            data-testid={'no-results-message'}
                                        />
                                    ) : (
                                        renderedTiles
                                    )}
                                </Box>
                                {!isListingItemsError ? (
                                    <>
                                        <Pagination initialPageCount={listingMetaData.pageCount} />
                                        <HStack
                                            css={styles.filtersInfoBox}
                                            gap={4}
                                            onClick={() => isMobileRes && toggleFiltersVisibility()}
                                        >
                                            <HStack as={'span'}>
                                                <IconsLinear.Settings />
                                            </HStack>
                                            <Text
                                                flex={1}
                                                dangerouslySetInnerHTML={{
                                                    __html: t('filtersInfo'),
                                                }}
                                            />
                                        </HStack>
                                    </>
                                ) : null}
                            </Box>
                        </Box>
                        {!searchParams?.size && (
                            <>
                                {cmsDescription ? (
                                    <ListingDescription description={cmsDescription} />
                                ) : null}
                                {cmsFaq ? <ListingFaq faqData={cmsFaq} /> : null}
                            </>
                        )}
                        {linkspace ? <SeoLinks linkSections={linkspace} mb={8} /> : null}
                        <StickySpinner isLoading={isSearchLoading} left={-4} right={-4} />
                    </GridItem>
                </GridWrapper>
                {isMobileRes && !isListingItemsError ? (
                    <SwitchStickyBar availableOptions={availableOptions} />
                ) : null}
            </main>
            <Support />
        </>
    );
};
